import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Trash2, Loader2 } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ModalCloseButton from '@/components/ModalCloseButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AccountDeletionModal
 *
 * Shows up when a logged-in user clicks "Delete My Account" on the
 * Privacy Policy page. Lists every per-user collection that will be
 * wiped and forces the user to type DELETE before the destructive
 * button is enabled — a standard belt-and-braces UX pattern for
 * irreversible operations.
 *
 * On success, calls onDeleted() (parent uses this to clear the auth
 * context and route the user back home).
 */
const ITEMS_TO_DELETE = [
  'Email address & login credentials',
  'Display name & profile picture',
  'Watchlist (every saved ticker)',
  'Portfolio positions (every share lot)',
  'Cash balance & full transaction history',
  'Monthly Income Goal & saved preferences',
  'Public share-page snapshot (your share link will stop working)',
  'Active sessions on every device (you will be signed out everywhere)',
];

const CONFIRM_PHRASE = 'DELETE';

export default function AccountDeletionModal({ open, onClose, onDeleted, userEmail }) {
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const phraseMatches = confirmText.trim().toUpperCase() === CONFIRM_PHRASE;

  const handleDelete = async () => {
    if (!phraseMatches || submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.delete(`${API}/auth/account`, { withCredentials: true });
      toast.success('Account permanently deleted', {
        description: 'All your data has been removed. Goodbye for now!',
        duration: 6000,
      });
      // Let the parent clean up auth context, then send them home.
      if (onDeleted) onDeleted(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to delete account. Please try again.';
      toast.error(msg);
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setConfirmText('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent
        className="max-w-lg w-[calc(100vw-1rem)] p-0 gap-0 overflow-hidden [&>button[data-testid='dialog-close-btn']]:hidden"
        data-testid="account-deletion-modal"
      >
        {/* Header */}
        <div className="bg-red-50 dark:bg-red-950/40 border-b border-red-200 dark:border-red-800 px-5 py-4 flex items-start justify-between gap-3">
          <div className="flex items-start gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-full bg-red-100 dark:bg-red-900/60 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-300" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-bold text-red-900 dark:text-red-200 break-words leading-tight">
                Permanently delete your account?
              </h2>
              <p className="text-[11px] sm:text-xs text-red-800/80 dark:text-red-300/80 mt-1">
                This cannot be undone. There is no recovery.
              </p>
            </div>
          </div>
          <ModalCloseButton onClick={handleClose} testId="account-deletion-close" />
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4 text-sm">
          {userEmail && (
            <p className="text-foreground/90">
              You&apos;re about to delete the account associated with{' '}
              <span className="font-semibold break-all">{userEmail}</span>.
            </p>
          )}

          <div>
            <p className="font-semibold text-foreground mb-2">The following will be hard-deleted from our database:</p>
            <ul className="space-y-1.5">
              {ITEMS_TO_DELETE.map((item) => (
                <li key={item} className="flex items-start gap-2 text-xs sm:text-[13px] leading-snug">
                  <Trash2 className="w-3.5 h-3.5 text-red-500 mt-0.5 shrink-0" />
                  <span className="break-words">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50/70 dark:bg-amber-950/30 p-3">
            <p className="text-xs text-amber-900 dark:text-amber-200 leading-relaxed">
              Your <strong>email address</strong> will be released — if you sign up again later, you&apos;ll start with a brand-new empty account. Public stock data we cache (Yahoo / Massive) is not user-specific and is unaffected.
            </p>
          </div>

          <div>
            <label htmlFor="account-deletion-confirm-input" className="block text-xs font-semibold text-foreground mb-1.5">
              To confirm, type <span className="font-mono text-red-600 dark:text-red-400">{CONFIRM_PHRASE}</span> below:
            </label>
            <Input
              id="account-deletion-confirm-input"
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={CONFIRM_PHRASE}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              disabled={submitting}
              className="font-mono"
              data-testid="account-deletion-confirm-input"
            />
          </div>
        </div>

        {/* Footer actions */}
        <div className="border-t bg-muted/30 px-5 py-3 flex flex-col sm:flex-row gap-2 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={submitting}
            data-testid="account-deletion-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={!phraseMatches || submitting}
            className="gap-1.5"
            data-testid="account-deletion-confirm-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Deleting…
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Yes, permanently delete my account
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
